import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Card, message, Spin } from 'antd';
import { PhoneOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';

const CallPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const { checkAuth } = useAuth();
  
  const phone = searchParams.get('phone');
  const code = searchParams.get('code');
  const userDataStr = searchParams.get('data');
  const leadName = searchParams.get('name');
  const lastContactTime = searchParams.get('lastContact');
  const leadId = searchParams.get('lead_id');

  // 状态管理
  const [currentPhone, setCurrentPhone] = useState(phone);
  const [currentLeadName, setCurrentLeadName] = useState(leadName);
  const [currentLastContactTime, setCurrentLastContactTime] = useState(lastContactTime);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [currentLeadId, setCurrentLeadId] = useState(leadId);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pollingStatus, setPollingStatus] = useState('等待中');

  // 修复可能的中文乱码（UTF-8被按Latin-1解码的情况）
  const maybeFixMojibake = (text) => {
    try {
      if (typeof text !== 'string' || text.length === 0) return text;
      const score = (t) => (t && t.match(/[\u4E00-\u9FFF]/g) || []).length;
      // 方法1：Latin-1→UTF-8（TextDecoder更稳健）
      const bytes = new Uint8Array(Array.from(text, ch => ch.charCodeAt(0) & 0xFF));
      let fixed = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
      if (score(fixed) > score(text)) return fixed;
      // 备选方法2：老式 escape/decode 兼容分支
      try {
        fixed = decodeURIComponent(escape(text));
        if (score(fixed) > score(text)) return fixed;
      } catch (_) {}
      return text;
    } catch (_) {
      return text;
    }
  };

  // 解析JWT payload，兼容base64url并保持UTF-8
  const decodeJwtPayloadUtf8 = (token) => {
    try {
      const part = token.split('.')[1];
      if (!part) return null;
      const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
      const pad = '='.repeat((4 - (b64.length % 4)) % 4);
      const base64 = b64 + pad;
      const binary = atob(base64);
      try {
        const bytes = Uint8Array.from(binary, ch => ch.charCodeAt(0));
        const jsonStr = new TextDecoder('utf-8').decode(bytes);
        return JSON.parse(jsonStr);
      } catch (_) {
        const jsonStr = decodeURIComponent(escape(binary));
        return JSON.parse(jsonStr);
      }
    } catch (_) {
      return null;
    }
  };

  // 使用useRef来存储当前状态，避免轮询依赖问题
  const currentStateRef = useRef({
    phone: phone,
    name: leadName,
    lastContactTime: lastContactTime,
    updatedAt: null
  });

  // 轮询获取最新线索信息（只有在登录成功后才开始）
  useEffect(() => {
    if (!isLoggedIn) {
      console.log('用户未登录，跳过轮询');
      return;
    }

    let retryCount = 0;
    const MAX_RETRIES = 3;
    const BASE_INTERVAL = 1000; // 基础轮询间隔
    const BACKOFF_MULTIPLIER = 1.5; // 退避乘数

    const fetchCurrentLead = async () => {
      try {
        setPollingStatus('轮询中...');
        // 直接调用 /leads/current 接口获取最新的线索信息
        // 不需要传递 lead_id，让服务器返回用户需要拨打的最新线索
        const url = `/leads/current`;
        console.log(`开始轮询获取最新线索信息: ${url}`);
        
        const response = await api.get(url);
        const leadData = response.data;
        
        console.log('轮询获取线索信息成功:', leadData);
        // 重置重试计数
        retryCount = 0;
        
        if (leadData) {
          const currentState = currentStateRef.current;
          
          // 检查是否有数据变化
          const hasDataChanged = 
            (leadData.phone && leadData.phone !== currentState.phone) ||
            (leadData.name && leadData.name !== currentState.name) ||
            (leadData.updatedAt && leadData.updatedAt !== currentState.updatedAt);
          
          console.log('数据变化检查:', { hasDataChanged, currentState, newData: leadData });
          
          if (hasDataChanged) {
            // 更新状态
            setCurrentPhone(leadData.phone);
            setCurrentLeadName(leadData.name);
            setCurrentLastContactTime(leadData.lastFollowUpAt ? 
              new Date(leadData.lastFollowUpAt).toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              }) : ''
            );
            setLastUpdatedAt(leadData.updatedAt);
            setCurrentLeadId(leadData.id || null);
            
            // 更新ref中的状态
            currentStateRef.current = {
              phone: leadData.phone,
              name: leadData.name,
              lastContactTime: leadData.lastFollowUpAt,
              updatedAt: leadData.updatedAt
            };
            
            // 不是首次加载时才显示消息
            if (!isFirstLoad) {
              message.success('已更新为新的线索信息');
              setPollingStatus('已更新');
              console.log('线索信息已更新:', leadData);
            } else {
              setIsFirstLoad(false);
              setPollingStatus('已连接');
              console.log('首次加载线索信息:', leadData);
            }
          } else {
            setPollingStatus('已连接');
            // 同步ID（即使数据未变化）
            setCurrentLeadId(leadData.id || null);
            console.log('线索信息无变化，保持连接');
          }
        } else {
          setPollingStatus('使用初始数据');
          console.log('未获取到线索信息，使用初始数据');
          // 如果没有获取到数据，使用URL中的初始数据
          if (phone && !currentStateRef.current.phone) {
            setCurrentPhone(phone);
            setCurrentLeadName(leadName || '');
            setCurrentLastContactTime(lastContactTime || '');
            currentStateRef.current = {
              phone: phone,
              name: leadName || '',
              lastContactTime: lastContactTime || '',
              updatedAt: null
            };
            console.log('使用初始数据:', { phone, leadName, lastContactTime });
          }
        }
      } catch (error) {
        console.error('获取当前线索信息失败:', error);
        console.error('错误详情:', error.response?.data || error.message);
        retryCount++;
        
        if (retryCount <= MAX_RETRIES) {
          setPollingStatus(`重连中(${retryCount}/${MAX_RETRIES})`);
          // 指数退避重试
          const retryDelay = BASE_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, retryCount - 1);
          console.log(`重试获取线索信息 (${retryCount}/${MAX_RETRIES})，延迟 ${retryDelay}ms`);
          setTimeout(fetchCurrentLead, retryDelay);
        } else {
          setPollingStatus('连接失败');
          console.error('获取线索信息失败，已达到最大重试次数');
          // 如果API调用失败，使用URL中的初始数据
          if (phone && !currentStateRef.current.phone) {
            setCurrentPhone(phone);
            setCurrentLeadName(leadName || '');
            setCurrentLastContactTime(lastContactTime || '');
            currentStateRef.current = {
              phone: phone,
              name: leadName || '',
              lastContactTime: lastContactTime || '',
              updatedAt: null
            };
            console.log('使用初始数据:', { phone, leadName, lastContactTime });
          }
        }
      }
    };

    // 首次加载
    fetchCurrentLead();

    // 每2秒轮询一次，平衡响应速度和服务器负载
    const interval = setInterval(fetchCurrentLead, BASE_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [isLoggedIn]); // 只依赖登录状态

  useEffect(() => {
    // 自动登录处理
    const autoLogin = async () => {
      // 1) 若存在一次性code，优先兑换并切换账号
      if (code) {
        try {
          const resp = await api.post('/auth/device-code/exchange', { code });
          const { token, user: userInfo } = resp.data || {};
          if (token && userInfo) {
            try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch(_){}
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userInfo));
            const displayUser = { ...userInfo, name: maybeFixMojibake(userInfo.name) || maybeFixMojibake(userInfo.username) || userInfo.username };
            setUser(displayUser);
            setIsLoggedIn(true);
            message.success('自动登录成功');
            const params = new URLSearchParams(window.location.search);
            params.delete('code');
            params.delete('data');
            navigate({ pathname: window.location.pathname, search: params.toString() }, { replace: true });
            return;
          }
        } catch (e) {
          console.warn('设备码兑换失败，将尝试旧参数方式');
        }
      }

      // 2) 兼容旧参数 data
      if (userDataStr) {
        try {
          let userData = null;
          try {
            const jsonUtf8 = decodeURIComponent(escape(atob(userDataStr)));
            userData = JSON.parse(jsonUtf8);
          } catch (_) {
            try {
              userData = JSON.parse(userDataStr);
            } catch (_) {
              const decodedData = decodeURIComponent(userDataStr);
              userData = JSON.parse(decodedData);
            }
          }
          const { token, user: userInfo } = userData || {};
          if (token && userInfo) {
            try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch(_){}
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(userInfo));
            const displayUser = { ...userInfo, name: maybeFixMojibake(userInfo.name) || maybeFixMojibake(userInfo.username) || userInfo.username };
            setUser(displayUser);
            setIsLoggedIn(true);
            message.success('自动登录成功');
            const params = new URLSearchParams(window.location.search);
            params.delete('data');
            navigate({ pathname: window.location.pathname, search: params.toString() }, { replace: true });
            return;
          }
        } catch (error) {
          console.error('自动登录失败:', error);
          message.error('登录信息解析失败，请重新扫描二维码');
          return;
        }
      }

      // 3) 否则沿用已有 token（用于刷新保持会话）
      const existingToken = localStorage.getItem('token');
      if (existingToken) {
        try {
          let cachedUser = null;
          try { cachedUser = JSON.parse(localStorage.getItem('user') || 'null'); } catch(_){}
          const payload = decodeJwtPayloadUtf8(existingToken) || {};
          const userInfo = cachedUser || payload || {};
          const displayUser = { ...userInfo, name: maybeFixMojibake(userInfo.name) || maybeFixMojibake(userInfo.username) || userInfo.username };
          setUser(displayUser);
          setIsLoggedIn(true);
        } catch(_) {}
        return;
      }
    };

    autoLogin();
  }, [code, userDataStr]); // 依赖URL参数变化

  const handleCall = async () => {
    if (!currentPhone) {
      message.error('无效的电话号码');
      return;
    }
    
    // 跟进记录创建函数，带重试机制
    const createFollowUp = async (maxRetries = 3) => {
      let retries = 0;
      const content = `拨打电话：${currentPhone}`;
      
      while (retries < maxRetries) {
        try {
          if (currentLeadId) {
            await api.post('/follow-ups', { lead_id: currentLeadId, content });
          } else if (currentPhone) {
            await api.post('/follow-ups/call', { phone: currentPhone, content });
          }
          console.log('跟进记录创建成功');
          return true;
        } catch (e) {
          retries++;
          console.warn(`记录拨打电话跟进失败（${retries}/${maxRetries}）：`, e?.message || e);
          if (retries < maxRetries) {
            // 指数退避重试
            const delay = 1000 * Math.pow(2, retries - 1);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      console.error('跟进记录创建失败，已达到最大重试次数');
      return false;
    };
    
    // 并行执行：创建跟进记录和拨打电话
    // 先尝试创建跟进记录，但即使失败也继续拨打电话
    createFollowUp().catch(err => {
      console.warn('跟进记录创建失败（忽略）：', err);
    });

    // 在移动端直接拨打电话（稍作延时，避免阻塞请求）
    setTimeout(() => {
      window.location.href = `tel:${currentPhone}`;
    }, 150);
  };

  const handleSwitchAccount = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (_) {}
    setUser(null);
    setIsLoggedIn(false);
    message.info('已退出，请重新扫码登录');
  };

  const handleBack = () => {
    navigate('/');
  };

  if (!currentPhone) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Card>
          <h3>无效的访问链接</h3>
          <p>请重新扫描二维码</p>
          <Button type="primary" onClick={handleBack}>
            返回
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '10px', 
      minHeight: '100vh', 
      background: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      paddingTop: '20px'
    }}>
      <Card style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '20px' }}>
          <PhoneOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
        </div>
        
        <h2 style={{ marginBottom: '10px' }}>拨打电话</h2>
        
        {user && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#52c41a', fontSize: '14px', marginBottom: '5px' }}>
              ✅ 已自动登录
            </p>
            <p style={{ color: '#666', fontSize: '12px' }}>
              当前用户：{(() => {
                const candidate = user.name || user.username || user.real_name || '未知用户';
                return maybeFixMojibake(candidate);
              })()}
            </p>
            <p style={{ color: '#1890ff', fontSize: '10px' }}>
              状态：{pollingStatus}
            </p>
            <Button size="small" onClick={handleSwitchAccount} style={{ marginTop: 8 }}>
              切换账号
            </Button>
          </div>
        )}
        
        {!user && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#ff4d4f', fontSize: '14px', marginBottom: '5px' }}>
              ❌ 未登录
            </p>
          </div>
        )}
        
        {currentLeadName && (
          <div style={{ 
            background: '#e6f7ff', 
            padding: '12px', 
            borderRadius: '8px',
            marginBottom: '15px',
            border: '1px solid #91d5ff'
          }}>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
              候选人：{currentLeadName}
            </p>
            {currentLastContactTime && (
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                上次联系：{currentLastContactTime}
              </p>
            )}
          </div>
        )}
        
        <div style={{ 
          background: '#f0f0f0', 
          padding: '15px', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#1890ff' }}>{currentPhone}</h3>
        </div>
        
        <Button
          type="primary"
          size="large"
          icon={<PhoneOutlined />}
          onClick={handleCall}
          style={{ 
            width: '100%', 
            height: '50px', 
            fontSize: '18px'
          }}
        >
          立即拨打
        </Button>
      </Card>
    </div>
  );
};

export default CallPage; 